import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  getSortedRowModel,
  SortingState,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { Pagination } from './pagination';
import Import from './import';
import LastFetchData from './lastFetchData';

import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';

interface TableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  pageSize?: number;
  isSwitch: boolean;
  isImport: boolean;
  isFocus?: boolean;
}

const Table = <T,>({
  data,
  columns,
  pageSize = 7,
  isSwitch,
  isImport,
  isFocus,
}: TableProps<T>) => {
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [sorting, setSorting] = useState<SortingState>([]);

  const { isProcess } = useSelector((state: RootState) => state.claims);
  const { importing } = useSelector((state: RootState) => state.import);
  const { isOpen } = useSelector((state: RootState) => state.cases);

  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, pageIndex, pageSize]);

  const totalPages = Math.ceil(data.length / pageSize);

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  useEffect(() => {
    setPageIndex(0);
  }, [isProcess, isOpen]);

  return (
    <div
      style={{ minHeight: `calc(100vh - ${isSwitch ? 250 : 198}px)` }}
      className={`w-full flex flex-col px-5 xl:px-0 max-w-[1120px] mx-auto mb-12`}>
      <div className="flex-1 overflow-auto">
        <table
          className={`
            w-full 
            border-separate
            border-spacing-y-1.5
            transition-opacity
            duration-200
            ${importing && isImport ? 'opacity-50 pointer-events-none' : ''}
          `}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                className="
                  bg-linear-[108.46deg,rgba(255,255,255,0.24)_0%,rgba(255,255,255,0.06)_100%]
                  shadow-[0px_5px_5.8px_-2px_rgba(31,43,64,0.06)]
                "
                key={headerGroup.id}>
                {headerGroup.headers.map((header, idx: number) => (
                  <th
                    style={{ width: `${header.getSize()}px` }}
                    className={`pt-2 pb-[9px] ${idx === 0 ? 'pl-17.5' : ''}`}
                    key={header.id}>
                    <div className="leading-[34px] text-base text-left text-[rgba(68,81,102,1)] font-medium font-jostMedium">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                className="
                  bg-linear-[108.46deg,rgba(255,255,255,0.24)_0%,rgba(255,255,255,0.06)_100%]
                  shadow-[0px_5px_5.8px_-2px_rgba(31,43,64,0.06)]
                "
                key={row.id}>
                {row.getVisibleCells().map((cell, idx: number) => (
                  <td
                    className={`py-3 ${idx === 0 ? 'pl-17.5' : ''}`}
                    key={cell.id}>
                    <div className="text-[15px] font-jostMedium font-medium leading-[18px] text-[rgba(31,43,64,0.8)]">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data?.length > 0 && !isFocus && (
        <div className="mt-auto flex flex-col items-center gap-4 py-6">
          {isImport ? <Import /> : <LastFetchData />}
          <Pagination
            pageIndex={pageIndex}
            totalPages={totalPages}
            setPageIndex={setPageIndex}
            disabled={importing && isImport}
          />
        </div>
      )}
    </div>
  );
};

export default Table;
